import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { fetchFastApi, ApiError } from "../api/fastapi";
import { OptroAccessListFlagSchema } from "../models/audit/optroAccess";
import { mergeOptroAccessUnavailable } from "../lib/optroAccessListFlag";

export type FieldworkSessionStatus = "in_progress" | "in_review" | "complete";

/**
 * The session fields BOTH entities have. Deliberately omits testPhase and
 * fyCycle: work_step_session has neither column, and that absence is a
 * documented domain decision ("Work Step Tests are name-only" —
 * work_step_session_record.py:14), not a gap to paper over.
 */
export const FieldworkSessionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  status: z
    .enum(["in_progress", "in_review", "complete"])
    .nullable()
    .optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  user: z
    .object({
      id: z.string(),
      email: z.string(),
    })
    .optional()
    .nullable(),
});

export type FieldworkSession = z.infer<typeof FieldworkSessionSchema>;

const FieldworkRowBase = {
  id: z.string().uuid(),
  name: z.string(),
  sessions: z.array(FieldworkSessionSchema).default([]),
};

export const FieldworkRowSchema = z.discriminatedUnion("type", [
  z.object({ ...FieldworkRowBase, type: z.literal("control") }),
  z.object({ ...FieldworkRowBase, type: z.literal("work_step") }),
]);

export type FieldworkRow = z.infer<typeof FieldworkRowSchema>;

const FieldworkSessionSummarySchema = z.object({
  total: z.number(),
  complete: z.number(),
  inProgress: z.number(),
  inReview: z.number(),
});

export type FieldworkSessionSummary = z.infer<
  typeof FieldworkSessionSummarySchema
>;

// Validates the entire response envelope in one parse, not just `rows`:
// fetchFastApi's JSON branch is typed `any`, so `totalCount`/`sessionSummary`
// would otherwise cross the Zod boundary unchecked, and an undefined
// totalCount would typecheck as `number` right up until a consumer called
// e.g. `.toLocaleString()` on it at render. sessionSummary is required (not
// optional) because the router always computes and returns it on every page,
// reset or load-more -- even though the hook below only *applies* the parsed
// summary to state on the reset path.
const FieldworkResponseSchema = z
  .object({
    rows: z.array(FieldworkRowSchema),
    totalCount: z.number(),
    sessionSummary: FieldworkSessionSummarySchema,
  })
  .merge(OptroAccessListFlagSchema);

interface FieldworkFilters {
  searchTerm?: string;
  sessionStatuses?: FieldworkSessionStatus[];
  testCreators?: string[];
  cycles?: string[];
  processes?: string[];
  controlOwners?: string[];
  testPhases?: string[];
  fyCycleIds?: string[];
  sections?: string[];
  subsections?: string[];
  preparers?: string[];
  opsAuditIds?: string[];
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

const LIMIT = 25;

/** Maps a filter key to its query-param name. Repeated params for arrays. */
const ARRAY_PARAMS: Array<[keyof FieldworkFilters, string]> = [
  ["sessionStatuses", "session_statuses"],
  ["testCreators", "created_by"],
  ["cycles", "cycles"],
  ["processes", "processes"],
  ["controlOwners", "control_owners"],
  ["testPhases", "test_phases"],
  ["fyCycleIds", "fy_cycle_ids"],
  ["sections", "sections"],
  ["subsections", "subsections"],
  ["preparers", "preparers"],
  ["opsAuditIds", "ops_audit_ids"],
];

// Hook for fetching the unified Fieldwork list (controls + work steps, each
// with their sessions) from GET /fieldwork/with-sessions. Modeled on
// useControlsWithSessions (src/hooks/useControlsV2.ts) — same LIMIT = 25
// offset paging and the same generationRef guard that discards a superseded
// reset fetch.
export const useFieldworkRows = (filters: FieldworkFilters) => {
  const [rows, setRows] = useState<FieldworkRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [sessionSummary, setSessionSummary] =
    useState<FieldworkSessionSummary | null>(null);
  // See OptroAccessListFlagSchema: linked rows are hidden from this list.
  // Held until the list resets (mergeOptroAccessUnavailable).
  const [optroAccessUnavailable, setOptroAccessUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);
  // Incremented on every reset fetch; used to discard results from stale
  // requests, e.g. a fast filter change superseding an in-flight fetch for
  // the previous filter set.
  const generationRef = useRef(0);
  // Guards against concurrent loadMore calls only. Must be a ref, not the
  // `loading` state: loadMore is a memoized callback, and a caller holding a
  // stale closure (e.g. an IntersectionObserver effect that doesn't list
  // loadMore in its deps) would otherwise see `loading` frozen at `false`
  // forever and never engage the guard. A duplicate in-flight request here
  // is not just wasted bandwidth: merge.py fetches offset+limit rows from
  // BOTH arms with all their sessions and sorts the union in Python on the
  // event loop, so a duplicate load-more is a second ~450-row fetch plus a
  // second in-request Python sort on the one endpoint documented as doing
  // exactly that kind of work on the loop.
  const isLoadingMoreRef = useRef(false);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchPage = useCallback(async (reset: boolean) => {
    if (!reset && isLoadingMoreRef.current) return;

    const generation = reset ? ++generationRef.current : generationRef.current;

    if (!reset) {
      isLoadingMoreRef.current = true;
    }

    const currentOffset = reset ? 0 : offsetRef.current;
    const currentFilters = filtersRef.current;

    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("limit", String(LIMIT));
      queryParams.append("offset", String(currentOffset));

      if (currentFilters.searchTerm) {
        queryParams.append("search", currentFilters.searchTerm);
      }
      for (const [key, param] of ARRAY_PARAMS) {
        const value = currentFilters[key];
        if (Array.isArray(value)) {
          value.forEach((v) => queryParams.append(param, v));
        }
      }
      if (currentFilters.sortBy) {
        queryParams.append("sort_by", currentFilters.sortBy);
      }
      if (currentFilters.sortOrder) {
        queryParams.append("sort_order", currentFilters.sortOrder);
      }

      const response = await fetchFastApi({
        path: `/fieldwork/with-sessions?${queryParams.toString()}`,
        method: "GET",
      });

      // Discard results from a superseded reset fetch.
      if (generationRef.current !== generation) return;

      // Parse the whole envelope in one shot -- see FieldworkResponseSchema.
      const parsed = FieldworkResponseSchema.parse(response);
      const data = parsed.rows;
      setTotalCount(parsed.totalCount);
      setOptroAccessUnavailable((current) =>
        mergeOptroAccessUnavailable(
          reset,
          current,
          parsed.optroAccessUnavailable,
        ),
      );
      if (reset) {
        setSessionSummary(parsed.sessionSummary);
      }

      setRows((prev) =>
        reset
          ? data
          : [
              ...prev,
              // Dedupe on id AND type: a control and a work step could in
              // principle carry the same uuid, and an id-only dedupe (as
              // useControlsWithSessions does, safely, since it only ever
              // holds one entity kind) would silently drop one here.
              ...data.filter(
                (r) => !prev.some((e) => e.id === r.id && e.type === r.type),
              ),
            ],
      );
      setHasMore(data.length === LIMIT);
      offsetRef.current = reset ? LIMIT : currentOffset + LIMIT;
    } catch (error) {
      if (generationRef.current !== generation) return;
      console.error("Failed to fetch fieldwork rows:", error);
      toast.error(
        error instanceof ApiError
          ? error.getUserMessage()
          : "Failed to fetch fieldwork data",
      );
    } finally {
      if (generationRef.current === generation) {
        setLoading(false);
      }
      if (!reset) {
        isLoadingMoreRef.current = false;
      }
    }
  }, []);

  // Shared by the reset effect below and by refetch(): both need the same
  // synchronous offsetRef/hasMore/rows reset before kicking off fetchPage(true).
  // fetchPage(true) also resets offsetRef once its response lands, but that is
  // not early enough on its own — a loadMore() bound to a stale pre-reset
  // closure can still fire in the window between this running and the next
  // render reflecting the new `loading`/`hasMore`. Zeroing offsetRef here means
  // that straggler reads the new offset (0) instead of the previous offset, so
  // at worst it re-requests page one (harmless — the id+type dedupe above
  // absorbs the duplicate) instead of silently and permanently skipping a page.
  // refetch() used to call fetchPage(true) directly without this bookkeeping,
  // which reopened exactly this gap for any loadMore racing a refetch.
  const resetAndFetch = useCallback(() => {
    offsetRef.current = 0;
    setHasMore(true);
    setRows([]);
    fetchPage(true);
  }, [fetchPage]);

  const serialisedFilters = JSON.stringify(filters);
  useEffect(() => {
    resetAndFetch();
    // serialisedFilters is the dependency: the filters object is rebuilt every
    // render, so depending on it directly would refetch on every render.
  }, [serialisedFilters, resetAndFetch]);

  const loadMore = useCallback(() => {
    if (isLoadingMoreRef.current || !hasMore) return;
    fetchPage(false);
  }, [hasMore, fetchPage]);

  return {
    rows,
    totalCount,
    sessionSummary,
    optroAccessUnavailable,
    loading,
    hasMore,
    loadMore,
    refetch: resetAndFetch,
  };
};
